/** A TipTap / ProseMirror document node, narrowed to what we traverse. */
export type RichNode = {
  type?: string;
  text?: string;
  content?: RichNode[];
  attrs?: Record<string, unknown>;
};

export type RichDoc = { type: "doc"; content?: RichNode[] };

export const EMPTY_DOC: RichDoc = { type: "doc", content: [] };

/** Node types that should force a line break when flattened. */
const BLOCK_TYPES = new Set([
  "paragraph",
  "heading",
  "listItem",
  "taskItem",
  "blockquote",
  "codeBlock",
  "horizontalRule",
  // Media atoms contribute alt text / transcripts, so they need a break too —
  // without one, an image caption runs straight into the next node's text.
  "image",
  "audio",
]);

/**
 * Flattens a TipTap document to plain text.
 *
 * This is what `notes.content_text` stores: it drives full-text search today and
 * is what an assistant reads later. Deliberately written here in `core` rather
 * than in the API so the client can compute an identical preview without a round
 * trip, and so it is unit-testable without a database.
 *
 * Media nodes contribute their alt text or transcript, so a note that is mostly
 * an image or a voice memo is still findable.
 */
export function flattenDoc(doc: unknown): string {
  const out: string[] = [];

  const walk = (node: RichNode | undefined): void => {
    if (!node || typeof node !== "object") return;

    if (typeof node.text === "string") out.push(node.text);

    const attrs = node.attrs ?? {};
    if (node.type === "image" && typeof attrs.alt === "string" && attrs.alt) {
      out.push(attrs.alt);
    }
    if (node.type === "audio" && typeof attrs.transcript === "string" && attrs.transcript) {
      out.push(attrs.transcript);
    }

    if (Array.isArray(node.content)) for (const child of node.content) walk(child);

    if (node.type && BLOCK_TYPES.has(node.type)) out.push("\n");
  };

  walk(doc as RichNode);

  return out
    .join("")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** First non-empty line, used to title an untitled note and to preview it. */
export function firstLine(text: string, max = 80): string {
  const line = text.split("\n").find((l) => l.trim().length > 0)?.trim() ?? "";
  return line.length > max ? `${line.slice(0, max - 1)}…` : line;
}
